/**
 * Хуки задач.
 *
 * Запросы не применяют клиентских фильтров, обходящих серверную
 * видимость: сервер (TaskService.listVisible / listForProject) сам
 * решает, что вернуть. Хук задачи проекта (useProjectTasksQuery)
 * ходит в GET /projects/:projectId/tasks — проектный эндпоинт,
 * который сервер проверяет по правилу C/D (404 при невидимом
 * проекте, все задачи при видимом). Дублирующая клиентская
 * фильтрация по «своим/чужим» отделам в хуках отсутствует.
 *
 * Мутации, влияющие на загрузку сотрудников (создание, изменение,
 * удаление, восстановление задач; добавление, изменение, удаление
 * логов), инвалидируют префиксный ключ ["reports"]: он покрывает
 * ["reports", "workload", …], ["reports", "period-capacity", …] и
 * ["reports", "employee-tasks", …]. Без этого после списания часов
 * раздел «Загрузка» показывал бы старые цифры до перезагрузки
 * вкладки. Мутации личных заметок загрузку не меняют — ["reports"]
 * не инвалидируют.
 */
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { api } from "../client";
import { useApiMutation } from "../../hooks/useApiMutation";

export const TASKS_KEY = ["tasks"];
export const PROJECT_TASKS_KEY = ["tasks", "project"];
export const MAX_TASKS_LIST_LIMIT = 5000;

export function useTasksQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: [...TASKS_KEY, params],
    queryFn: () => api.get("/tasks", { params }),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useProjectTasksQuery(projectId, params = {}, options = {}) {
  const enabled = !!projectId;
  return useQuery({
    queryKey: [...PROJECT_TASKS_KEY, projectId, params],
    queryFn: () => api.get(`/projects/${projectId}/tasks`, { params }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useDraftsTasksQuery(options = {}) {
  return useQuery({
    queryKey: [...TASKS_KEY, "drafts"],
    queryFn: () =>
      api.get("/tasks", {
        params: { includeDrafts: true, limit: MAX_TASKS_LIST_LIMIT },
      }),
    select: (raw) => {
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.items)) return raw.items;
      return [];
    },
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useTaskQuery(id, options = {}) {
  return useQuery({
    queryKey: ["task", id],
    queryFn: () => api.get(`/tasks/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useSubtasksQuery(taskId, options = {}) {
  return useQuery({
    queryKey: [...TASKS_KEY, "subtasks", taskId],
    queryFn: () => api.get(`/tasks/${taskId}/subtasks`),
    enabled: !!taskId,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useCreateTaskMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ body, needsConfirmation, __idempotencyKey }) => {
      const headers = {};
      if (needsConfirmation) headers["X-Needs-Confirmation"] = "true";
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.post("/tasks", body, { headers });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["assignment-approvals"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useUpdateTaskMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({
      id,
      body,
      needsConfirmation,
      version,
      __idempotencyKey,
    }) => {
      const headers = {};
      if (needsConfirmation) headers["X-Needs-Confirmation"] = "true";
      if (version !== undefined && version !== null)
        headers["If-Match"] = String(version);
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.patch(`/tasks/${id}`, body, { headers });
    },
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: TASKS_KEY });
      await qc.cancelQueries({ queryKey: ["task", id] });

      const previousTask = qc.getQueryData(["task", id]);
      const previousLists = qc.getQueriesData({ queryKey: TASKS_KEY });

      const patch = {};
      if (body.startDate !== undefined) patch.start = body.startDate;
      if (body.deadline !== undefined) patch.deadline = body.deadline;

      const updateCache = (list) => {
        if (!Array.isArray(list)) return list;

        const oldParent = list.find((t) => t.id === id);
        if (!oldParent) return list;

        const newParent = { ...oldParent, ...body, ...patch };

        const childrenMap = new Map();
        for (const t of list) {
          if (t.parentTaskId) {
            if (!childrenMap.has(t.parentTaskId))
              childrenMap.set(t.parentTaskId, []);
            childrenMap.get(t.parentTaskId).push(t);
          }
        }

        let deltaStart = 0;
        if (oldParent.start && newParent.start) {
          deltaStart = Math.round(
            (new Date(newParent.start).getTime() -
              new Date(oldParent.start).getTime()) /
              86400000,
          );
        }
        let deltaEnd = 0;
        if (oldParent.deadline && newParent.deadline) {
          deltaEnd = Math.round(
            (new Date(newParent.deadline).getTime() -
              new Date(oldParent.deadline).getTime()) /
              86400000,
          );
        }

        const isMove =
          deltaStart === deltaEnd && oldParent.start && newParent.start;

        const shiftDate = (iso, days) => {
          if (!iso) return iso;
          const d = new Date(iso);
          d.setUTCDate(d.getUTCDate() + days);
          return d.toISOString().slice(0, 10);
        };

        const updates = new Map();
        updates.set(id, newParent);

        const applyShift = (parentId, currentParent) => {
          const children = childrenMap.get(parentId) || [];
          for (const child of children) {
            let nextStart = child.start;
            let nextDeadline = child.deadline;

            if (isMove) {
              if (nextStart) nextStart = shiftDate(nextStart, deltaStart);
              if (nextDeadline)
                nextDeadline = shiftDate(nextDeadline, deltaStart);
            } else {
              if (
                currentParent.start &&
                nextStart &&
                nextStart < currentParent.start
              )
                nextStart = currentParent.start;
              if (
                currentParent.deadline &&
                nextDeadline &&
                nextDeadline > currentParent.deadline
              )
                nextDeadline = currentParent.deadline;
            }

            if (nextStart && nextDeadline && nextStart > nextDeadline) {
              nextStart = nextDeadline;
            }

            const updatedChild =
              nextStart !== child.start || nextDeadline !== child.deadline
                ? { ...child, start: nextStart, deadline: nextDeadline }
                : child;

            updates.set(child.id, updatedChild);
            applyShift(child.id, updatedChild);
          }
        };

        applyShift(id, newParent);
        return list.map((t) => updates.get(t.id) || t);
      };

      qc.setQueryData(["task", id], (old) =>
        old ? { ...old, ...body, ...patch } : old,
      );

      qc.setQueriesData({ queryKey: TASKS_KEY }, (old) => {
        if (!old) return old;
        if (Array.isArray(old)) return updateCache(old);
        if (old.items && Array.isArray(old.items))
          return { ...old, items: updateCache(old.items) };
        if (old.tasks && Array.isArray(old.tasks))
          return { ...old, tasks: updateCache(old.tasks) };
        return old;
      });

      return { previousTask, previousLists };
    },
    onError: (_err, { id }, context) => {
      // При ошибке (например, нехватка часов при сжатии) откатываем все изменения
      if (context?.previousTask) {
        qc.setQueryData(["task", id], context.previousTask);
      }
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          qc.setQueryData(key, data);
        }
      }
    },
    onSuccess: (data, { id }) => {
      // Обновляем карточку задачи данными из ответа сервера
      if (data) {
        qc.setQueryData(["task", id], data);
      }

      // Обновляем задачу в списках (на случай, если сервер изменил другие поля, например plannedHours)
      qc.setQueriesData({ queryKey: TASKS_KEY }, (old) => {
        if (!old) return old;
        const updateList = (list) => {
          if (!Array.isArray(list)) return list;
          return list.map((t) => (t.id === id ? { ...t, ...data } : t));
        };
        if (Array.isArray(old)) return updateList(old);
        if (old.items && Array.isArray(old.items))
          return { ...old, items: updateList(old.items) };
        if (old.tasks && Array.isArray(old.tasks))
          return { ...old, tasks: updateList(old.tasks) };
        return old;
      });

      // Инвалидируем только те данные, которые не участвуют в оптимистичном обновлении
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["assignment-approvals"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteTaskMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.delete(`/tasks/${id}`, { headers });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useRestoreTaskMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.post(`/tasks/${id}/restore`, null, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useAddTaskLogMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body, needsConfirmation, __idempotencyKey }) => {
      const headers = {};
      if (needsConfirmation) headers["X-Needs-Confirmation"] = "true";
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.post(`/tasks/${id}/logs`, body, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useUpdateTaskLogMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, logId, body, needsConfirmation, __idempotencyKey }) => {
      const headers = {};
      if (needsConfirmation) headers["X-Needs-Confirmation"] = "true";
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.patch(`/tasks/${id}/logs/${logId}`, body, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useDeleteTaskLogMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, logId, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.delete(`/tasks/${id}/logs/${logId}`, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useCreateTaskNoteMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.post(`/tasks/${id}/notes`, body, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
    },
  });
}

export function useUpdateTaskNoteMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, noteId, body, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.patch(`/tasks/${id}/notes/${noteId}`, body, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
    },
  });
}

export function useDelegationTasksQuery(userId, options = {}) {
  return useQuery({
    queryKey: ["tasks", "delegation", userId],
    queryFn: () =>
      api.get("/tasks", {
        params: { assigneeId: userId, limit: 500 },
      }),
    select: (raw) => {
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.items)) return raw.items;
      return [];
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useDeleteTaskNoteMutation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, noteId, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.delete(`/tasks/${id}/notes/${noteId}`, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: TASKS_KEY });
      qc.invalidateQueries({ queryKey: ["task", id] });
    },
  });
}
